import React, { useState, useEffect, useRef } from 'react';
import { useResponsive } from '../utils/useResponsive';
import { auth } from '../firebase/config';
import { createSupportTicket, addMessageToTicket, getSupportTicket } from '../services/supportService';
import { uiConfig, companyConfig, apiConfig } from '../config/app';

const SupportAvatar = ({ size = 32 }) => (
  <div style={{
    width: size,
    height: size,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, var(--primary, #33475b) 0%, var(--accent, #ff7a59) 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    color: '#fff',
    fontWeight: 700,
    fontSize: size * 0.4,
    fontFamily: 'inherit',
    letterSpacing: '-0.02em',
    userSelect: 'none'
  }}>
    S
  </div>
);

const ChatSupport = ({ isOpen, onClose }) => {
  const { isMobile } = useResponsive();
  const [isMinimized, setIsMinimized] = useState(false);
  const [isSupportOnline, setIsSupportOnline] = useState(false);
  
  // Check if support is configured (URL must be set and not localhost; key must be non-empty)
  const supportApiUrl = (apiConfig.supportApiUrl || '').trim();
  const supportApiKey = (apiConfig.supportApiKey || '').trim();
  const isSupportConfigured = supportApiKey.length > 0 && supportApiUrl.length > 0 &&
    supportApiUrl !== 'http://localhost:5001/api' && !supportApiUrl.startsWith('http://localhost:');

  const supportNotConfiguredMessage = () => {
    return 'Support chat not yet connected. Contact ' + companyConfig.supportEmail + ' for assistance.';
  };

  const [messages, setMessages] = useState([
    {
      id: 1,
      text: isSupportConfigured
        ? uiConfig.welcomeMessage
        : supportNotConfiguredMessage(),
      sender: 'support',
      timestamp: new Date()
    }
  ]);
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentTicketId, setCurrentTicketId] = useState(() => {
    try {
      return sessionStorage.getItem('supportTicketId') || null;
    } catch {
      return null;
    }
  });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch support online status from Jupiter API, re-poll every 60 s while chat is open
  useEffect(() => {
    if (!isSupportConfigured) return;

    const fetchPresence = async () => {
      try {
        const url = `${supportApiUrl}/supportPresenceGet?apiKey=${encodeURIComponent(supportApiKey)}`;
        const res = await fetch(url, { method: 'GET' });
        if (res.ok) {
          const data = await res.json();
          setIsSupportOnline(data.isOnline === true);
        } else {
          console.warn('[ChatSupport] supportPresenceGet returned', res.status, '— is the function deployed?');
        }
      } catch (err) {
        console.warn('[ChatSupport] supportPresenceGet fetch failed:', err);
      }
    };

    fetchPresence();
    if (!isOpen) return;
    const intervalId = setInterval(fetchPresence, 60000);
    return () => clearInterval(intervalId);
  }, [isOpen, isSupportConfigured, supportApiUrl, supportApiKey]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Persist ticket ID so reopening the chat keeps the same conversation
  useEffect(() => {
    try {
      if (currentTicketId) {
        sessionStorage.setItem('supportTicketId', currentTicketId);
      } else {
        sessionStorage.removeItem('supportTicketId');
      }
    } catch (e) {
      // ignore
    }
  }, [currentTicketId]);

  // Poll for agent replies when we have an open ticket
  useEffect(() => {
    if (!isOpen || !currentTicketId || !isSupportConfigured) return;
    const poll = async () => {
      try {
        const data = await getSupportTicket(currentTicketId);
        console.log('[SupportChat] poll response:', JSON.stringify(data?.ticket?.messages));
        const ticketMessages = data?.ticket?.messages;
        if (!Array.isArray(ticketMessages)) return;
        setMessages((prev) => {
          const welcome = prev[0]?.sender === 'support' && prev[0]?.id === 1 ? prev[0] : null;
          const fromApi = ticketMessages.map((msg, idx) => ({
            id: `ticket-${idx}-${msg.timestamp || idx}`,
            text: msg.text || '',
            sender: msg.sender === 'agent' || msg.sender === 'support' ? 'support' : 'user',
            timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date()
          }));
          // Always keep the welcome message at the top, then all messages from the ticket
          return welcome ? [welcome, ...fromApi] : fromApi;
        });
      } catch (err) {
        console.error('[SupportChat] poll error:', err);
      }
    };
    poll();
    const intervalId = setInterval(poll, 3000);
    return () => clearInterval(intervalId);
  }, [isOpen, currentTicketId, isSupportConfigured]);

  const generateSubject = (message) => {
    const words = message.trim().split(/\s+/);
    const firstWords = words.slice(0, 6).join(' ');
    return firstWords.length < 50 ? firstWords : firstWords.substring(0, 47) + '...';
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || isLoading) return;

    // Check if support is configured
    if (!isSupportConfigured) {
      setError('Chat support is not yet configured. Please contact ' + companyConfig.supportEmail + ' directly.');
      return;
    }

    // Check if user is authenticated
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setError('Please log in to send a support request.');
      return;
    }

    const userMessage = {
      id: Date.now(),
      text: newMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const messageText = newMessage;
    setNewMessage('');
    setIsTyping(true);
    setIsLoading(true);
    setError(null);

    try {
      // Get user information
      const userData = {
        uid: currentUser.uid,
        email: currentUser.email || '',
        displayName: currentUser.displayName || '',
        role: '' // Can be extended if role is stored in user context
      };

      // Check if this is a new conversation or follow-up
      if (!currentTicketId) {
        // Create new ticket (first message)
        const subject = generateSubject(messageText);
        const result = await createSupportTicket({
          message: messageText,
          subject,
          user: userData
        });

        // Save the ticket ID for future messages
        setCurrentTicketId(result.ticketId);

        // Show success message only for the first ticket
        const supportMessage = {
          id: Date.now() + 1,
          text: uiConfig.ticketConfirmationMessage(result.ticketId),
          sender: 'support',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, supportMessage]);
      } else {
        // Add message to existing ticket (follow-up message)
        await addMessageToTicket(currentTicketId, messageText, userData);
        // Message is already shown in the UI (userMessage added above)
        // No confirmation message needed for follow-ups
      }
    } catch (err) {
      const isFollowUp = !!currentTicketId;
      console.error(isFollowUp ? 'Error adding message:' : 'Error creating support ticket:', err);
      setError(err.message || (isFollowUp ? 'Failed to send message. Please try again.' : 'Failed to create support ticket. Please try again.'));
      
      // Show error message in chat
      const errorMessage = {
        id: Date.now() + 1,
        text: err.message || uiConfig.errorMessages.support,
        sender: 'support',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
      setIsLoading(false);
    }
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const quickReplies = [
    "I need help with booking",
    "Technical issue",
    "Billing question",
    "General inquiry"
  ];

  const handleQuickReply = (reply) => {
    setNewMessage(reply);
    // Focus the input field after setting the message
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  // Check authentication when dialog opens
  useEffect(() => {
    if (isOpen) {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setError('Please log in to use the support chat.');
      } else {
        setError(null);
      }
    }
  }, [isOpen]);

  // Handle starting a new conversation
  const handleNewConversation = () => {
    if (currentTicketId || messages.length > 1) {
      const confirmed = window.confirm('Start a new conversation? This will create a new support ticket.');
      if (!confirmed) return;
    }
    try {
      sessionStorage.removeItem('supportTicketId');
    } catch (e) {
      // ignore
    }
    setCurrentTicketId(null);
    setMessages([{
      id: 1,
      text: uiConfig.welcomeMessage,
      sender: 'support',
      timestamp: new Date()
    }]);
    setNewMessage('');
    setError(null);
  };

  if (!isOpen) return null;

  const currentUser = auth.currentUser;
  if (!currentUser) {
    return (
      <div className={`chat-support-overlay${isMinimized ? ' minimized' : ''}`}>
        <div className={`chat-support-container ${isMobile ? 'mobile' : ''}`}>
          <div className="chat-support-header">
            <div className="chat-support-header-info">
              <SupportAvatar size={38} />
              <div className="chat-support-header-text">
                <h3>{companyConfig.supportName}</h3>
                <span className="chat-support-status">
                  <div className={`status-indicator ${isSupportOnline ? 'online' : 'offline'}`}></div>
                  {isSupportOnline ? 'Online' : 'Offline'}
                </span>
              </div>
            </div>
            <div className="chat-support-header-actions">
              <button type="button" className="chat-support-minimise" onClick={() => setIsMinimized(v => !v)} aria-label={isMinimized ? 'Expand chat' : 'Minimise chat'}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  {isMinimized
                    ? <polyline points="18 15 12 9 6 15"></polyline>
                    : <polyline points="6 9 12 15 18 9"></polyline>}
                </svg>
              </button>
              <button type="button" className="chat-support-close" onClick={onClose} aria-label="Close chat">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          </div>
          {!isMinimized && (
            <div className="chat-auth-gate">
              <div className="chat-auth-icon">
                <SupportAvatar size={56} />
              </div>
              <p className="chat-auth-title">Sign in required</p>
              <p className="chat-auth-subtitle">Please log in to chat with our support team.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`chat-support-overlay${isMinimized ? ' minimized' : ''}`}>
      <div className={`chat-support-container ${isMobile ? 'mobile' : ''}`}>

        {/* Header */}
        <div className="chat-support-header">
          <div className="chat-support-header-info">
            <div className="chat-support-header-text">
              <h3>{companyConfig.supportName}</h3>
              <span className="chat-support-status">
                <div className={`status-indicator ${isSupportOnline ? 'online' : 'offline'}`}></div>
                {isSupportOnline ? 'Online · Typically replies in 15 mins' : 'Offline · Leave a message'}
              </span>
            </div>
          </div>
          <div className="chat-support-header-actions">
            {currentTicketId && !isMinimized && (
              <button
                type="button"
                className="chat-header-action-btn"
                onClick={handleNewConversation}
                aria-label="Start new conversation"
                title="New conversation"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                <span>New</span>
              </button>
            )}
            <button type="button" className="chat-support-minimise" onClick={() => setIsMinimized(v => !v)} aria-label={isMinimized ? 'Expand chat' : 'Minimise chat'}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                {isMinimized
                  ? <polyline points="18 15 12 9 6 15"></polyline>
                  : <polyline points="6 9 12 15 18 9"></polyline>}
              </svg>
            </button>
            <button type="button" className="chat-support-close" onClick={onClose} aria-label="Close chat">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>

        {/* Messages */}
        {!isMinimized && <div className="chat-support-messages">
          {messages.map((message, index) => {
            const isUser = message.sender === 'user';
            const prevMessage = messages[index - 1];
            const showAvatar = !isUser && (!prevMessage || prevMessage.sender !== 'support');
            return (
              <div key={message.id} className={`chat-message ${isUser ? 'user' : 'support'}`}>
                {!isUser && (
                  <div className="chat-message-avatar">
                    {showAvatar ? <SupportAvatar size={28} /> : <div style={{ width: 28 }} />}
                  </div>
                )}
                <div className="chat-message-bubble-wrap">
                  <div className="chat-message-content">
                    <p>{message.text}</p>
                  </div>
                  <span className="chat-message-time">
                    {formatTime(message.timestamp)}
                    {isUser && (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginLeft: 3, opacity: 0.7 }}>
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    )}
                  </span>
                </div>
              </div>
            );
          })}

          {isTyping && (
            <div className="chat-message support">
              <div className="chat-message-avatar">
                <SupportAvatar size={28} />
              </div>
              <div className="chat-message-bubble-wrap">
                <div className="chat-message-content typing-bubble">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>}

        {/* Quick Replies */}
        {!isMinimized && messages.length === 1 && isSupportConfigured && (
          <div className="chat-quick-replies">
            <p className="quick-replies-label">Suggested topics</p>
            <div className="quick-replies-chips">
              {quickReplies.map((reply, index) => (
                <button
                  key={index}
                  type="button"
                  className="quick-reply-chip"
                  onClick={() => handleQuickReply(reply)}
                >
                  {reply}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Error Message */}
        {!isMinimized && error && (
          <div className="chat-error-banner">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            {error}
          </div>
        )}

        {/* Input */}
        {!isMinimized && <form className="chat-support-input-form" onSubmit={handleSendMessage}>
          <div className="chat-input-container">
            <input
              ref={inputRef}
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={isSupportConfigured ? "Type a message..." : "Support not configured"}
              className="chat-input"
              maxLength={500}
              disabled={isLoading || !isSupportConfigured}
            />
            <button
              type="submit"
              className="chat-send-btn"
              disabled={!newMessage.trim() || isLoading || !isSupportConfigured}
              aria-label="Send message"
            >
              {isLoading ? (
                <div className="chat-send-spinner"></div>
              ) : (
                <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                </svg>
              )}
            </button>
          </div>
          <div className="chat-input-footer">
            <span className="char-count" style={{ opacity: newMessage.length > 400 ? 1 : 0.5, color: newMessage.length > 450 ? '#ef4444' : 'inherit' }}>
              {newMessage.length}/500
            </span>
            <span className="powered-by">{uiConfig.poweredBy}</span>
          </div>
        </form>}
      </div>
    </div>
  );
};

export default ChatSupport;
